import { type FC, useMemo, useState } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";

import Button from "../button/Button";
import Modal from "../modal/Modal";
import TranscriptionOptionsForm, {
  DEFAULT_TRANSCRIPTION_OPTIONS,
  toTranscriptionOptions,
  type TranscriptionOptionsValue,
} from "./TranscriptionOptionsForm";
import {
  useStartBulkTranscriptionMutation,
  type BulkTranscriptionSkipped,
} from "../../app/services/mediaTranscription";

/**
 * 一括文字起こしモーダル。
 *
 * 各一覧（共有ライブラリ / トークルーム / メッセンジャー内フォルダ /
 * ショット編集 / 作品登録）の三点リーダー →「一括文字起こし」から開く。
 *
 * 対象は呼び出し側が集めた音声・動画ファイル。すでに文字起こし済み・実行中・
 * 非対応形式のものは BE 側で自動的にスキップされるので、ここでは絞り込まずに
 * そのまま渡す。上限（件数 / 合計再生時間）を超える場合は BE が 1 件も
 * 実行せずエラーを返す。
 */

/** 選択件数が多いときに注意を促すしきい値（BE の上限とは別の目安） */
const LARGE_SELECTION_THRESHOLD = 20;

export interface BulkTranscriptionTarget {
  /** バックエンドの横断レジストリで解決される fileId */
  fileId: string;
  /** 再生時間（秒）。分かる場合のみ。合計時間の目安表示に使う */
  durationSeconds?: number | null;
}

export interface BulkTranscriptionModalProps {
  /** 対象ファイル（音声・動画のみを呼び出し側で抽出して渡す） */
  targets: BulkTranscriptionTarget[];
  /** 「フォルダ「音源」」のような対象範囲の説明。モーダルの説明に出す */
  scopeLabel?: string;
  onClose: () => void;
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  text-align: center;
`;

const BodyText = styled.div.attrs({
  className: "bodytext-13 u-line-height-150",
})`
  color: var(--soremo-gray);
  white-space: pre-line;
`;

const SubText = styled.div.attrs({
  className: "bodytext-11 u-line-height-150",
})`
  color: var(--soremo-light-gray);
  white-space: pre-line;
`;

const WarningText = styled.div.attrs({
  className: "bodytext-11 u-line-height-150",
})`
  color: var(--soremo-green);
  white-space: pre-line;
`;

const FooterActions = styled.div.attrs({ className: "u-mt16" })`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

/** 秒を「約 X 時間 Y 分」に整形する（0 秒は null） */
const formatTotalDuration = (seconds: number): string | null => {
  if (seconds <= 0) return null;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  if (hours > 0) return `約 ${hours} 時間 ${minutes} 分`;
  return `約 ${Math.max(1, minutes)} 分`;
};

/** BE が返すスキップ内訳を、ユーザー向けの一文にまとめる */
const describeSkipped = (skipped: BulkTranscriptionSkipped): string => {
  const parts: string[] = [];
  if (skipped.already_done > 0) parts.push(`済み ${skipped.already_done} 件`);
  if (skipped.running > 0) parts.push(`実行中 ${skipped.running} 件`);
  if (skipped.unsupported > 0) parts.push(`非対応 ${skipped.unsupported} 件`);
  if (skipped.too_long > 0) parts.push(`長さ超過 ${skipped.too_long} 件`);
  // 権限が無い / すでに削除されたファイル。件数だけ黙って減らさず理由を出す。
  if (skipped.no_permission > 0)
    parts.push(`対象外 ${skipped.no_permission} 件`);
  return parts.join(" / ");
};

const BulkTranscriptionModal: FC<BulkTranscriptionModalProps> = ({
  targets,
  scopeLabel,
  onClose,
}) => {
  const [options, setOptions] = useState<TranscriptionOptionsValue>(
    DEFAULT_TRANSCRIPTION_OPTIONS,
  );
  const [startBulkTranscription, { isLoading: isStarting }] =
    useStartBulkTranscriptionMutation();

  const fileIds = useMemo(
    () => Array.from(new Set(targets.map((t) => t.fileId).filter(Boolean))),
    [targets],
  );

  // 再生時間が分かっているものだけ合算する（未取得のファイルは 0 扱い）。
  // BE も同じ方針で probe 済みのぶんだけを上限判定に使う。
  const knownTotalSeconds = useMemo(
    () => targets.reduce((sum, t) => sum + (t.durationSeconds ?? 0), 0),
    [targets],
  );
  const totalDurationText = formatTotalDuration(knownTotalSeconds);

  const handleStart = async () => {
    if (isStarting || fileIds.length === 0) return;
    try {
      const result = await startBulkTranscription({
        fileIds,
        ...toTranscriptionOptions(options),
      }).unwrap();

      const { started, skipped } = result.data;
      const skippedText = describeSkipped(skipped);
      if (started === 0) {
        toast.success(
          skippedText
            ? `実行対象がありませんでした（${skippedText}）`
            : "実行対象がありませんでした",
        );
      } else {
        toast.success(
          skippedText
            ? `${started} 件の文字起こしを開始しました（スキップ: ${skippedText}）`
            : `${started} 件の文字起こしを開始しました`,
        );
      }
      onClose();
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        "一括文字起こしの開始に失敗しました。時間をおいてお試しください。";
      toast.error(message);
    }
  };

  const isLarge = fileIds.length > LARGE_SELECTION_THRESHOLD;

  return (
    <Modal
      onClose={onClose}
      titleSize="compact"
      title="一括文字起こし"
      description={scopeLabel}
      descriptionTone="subtle"
      size="auto"
    >
      <Body>
        <BodyText>
          {`音声・動画 ${fileIds.length} 件を文字起こしします。\n結果はこのファイルを閲覧できるメンバー全員に共有されます。`}
        </BodyText>
        {totalDurationText && (
          <SubText>{`合計の長さ: ${totalDurationText}`}</SubText>
        )}
        <SubText>
          すでに文字起こし済み・実行中・非対応形式のファイルは自動でスキップされます。
        </SubText>
        {isLarge && (
          <WarningText>
            {
              "件数が多いため、順番に処理されます。すべて完了するまで数時間かかることがあります。\n画面を閉じても処理は続行され、一覧のステータスが自動で更新されます。"
            }
          </WarningText>
        )}
        <TranscriptionOptionsForm
          value={options}
          onChange={setOptions}
          disabled={isStarting}
        />
        <FooterActions>
          <Button
            size="small"
            theme="secondary"
            variant="text"
            width="fit"
            disabled={isStarting}
            onClick={onClose}
          >
            キャンセル
          </Button>
          <Button
            size="small"
            theme="primary"
            variant="filled"
            width="fit"
            leftIcon={isStarting ? "hourglass_empty" : "speech_to_text"}
            disabled={isStarting || fileIds.length === 0}
            onClick={handleStart}
          >
            {isStarting ? "送信中…" : "文字起こしを開始"}
          </Button>
        </FooterActions>
      </Body>
    </Modal>
  );
};

export default BulkTranscriptionModal;
